import { useContext } from 'react';
import styled from 'styled-components';
import { SubRDataContext } from '../dataProvider';
import ContentLoader from 'react-content-loader';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../../state/store';
import { formatDateWithTimezoneOffset } from '../../../../../helpers/utils';
import LoadingErrorScreen from '../loadingError';
import SubRExportReportBtn from '../../ExportReportBtn';

export default function AuditLogs() {
  const data = useContext(SubRDataContext) as any;
  const theme = useSelector((state: RootState) => state.theme.mode);
  const tzOffset = useSelector(
    (state: RootState) => state?.company?.company_details?.time_zone_offset
  );
  const effectiveOffset = data?.timezoneOffset || tzOffset || '+00:00';

  const handleExportClick = () => {
    if (!data?.auditLogs?.length) return;

    const headers = ['Activity', 'Staff', 'Date & Time', 'Activity Summary'];
    const rows = data.auditLogs.map((item: any) => [
      item.event?.replace(/_/g, ' ') || '',
      item.user_name || '-',
      formatDateWithTimezoneOffset(
        item.business_date_time,
        item?.offset || effectiveOffset,
        'dd LLL yyyy - h:mm a'
      ),
      item.event_summary || ''
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((value: string) => {
            const stringValue = String(value ?? '');
            if (
              stringValue.includes(',') ||
              stringValue.includes('"') ||
              stringValue.includes('\n')
            ) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          })
          .join(',')
      )
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Order_${data?.order_no || 'audit_logs'}_Audit_Log.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Error state
  if (data?.auditLogsDataError && !data?.auditLogs) {
    return (
      <Container>
        <div className="error-screen">
          <LoadingErrorScreen />
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="header">
        <div className="header-text">
          <h3>Order Log</h3>
          <p>This is the complete audit trail for Order Number {data?.order_no}</p>
        </div>
        {!!data?.auditLogs?.length && (
          <div className="header-action">
            <SubRExportReportBtn onClick={handleExportClick} />
          </div>
        )}
      </div>

      {data?.isAuditLogsDataLoading && !data?.auditLogs ? (
        <div className="loader-container">
          <ContentLoader
            height="100%"
            width="100%"
            speed={3}
            backgroundColor={theme === 'light' ? '#f3f3f3' : '#2a2a2a'}
            foregroundColor={theme === 'light' ? '#ecebeb' : '#1e1e1e'}
            style={{ width: '100%', height: '100%' }}
          >
            <rect x="0" y="0" rx="0" ry="0" width="100%" height="100%" />
          </ContentLoader>
        </div>
      ) : (
        <>
          {data?.auditLogs && data?.auditLogs.length ? (
            <div className="table-container">
              <div className="table-header">
                <span>Activity</span>
                <span>Staff</span>
                <span>Date & Time</span>
                <span>Activity Summary</span>
              </div>
              <div className="table-body">
                {data?.auditLogs.map((item: any, index: number) => (
                  <div className="table-row" key={index}>
                    <span>{item.event?.replace(/_/g, ' ')}</span>
                    <span>{item.user_name || '-'}</span>
                    <span>
                      {formatDateWithTimezoneOffset(
                        item.business_date_time,
                        item?.offset || effectiveOffset,
                        'dd LLL yyyy - h:mm a'
                      )}
                    </span>
                    <span>{item.event_summary}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="no-data">No data available</div>
          )}
        </>
      )}
    </Container>
  );
}

const Container = styled.div`
  padding: 0.5rem;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.widget.background};
  color: ${({ theme }) => theme.text};
  border: 1px solid rgb(255, 255, 255);
  border-radius: 0.5rem;
  filter: drop-shadow(rgba(0, 0, 0, 0.1) 0px 4px 4px);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  .header {
    margin-bottom: 0.75rem;
    display: flex !important;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: 0.75rem;
    flex-shrink: 0;

    .header-text {
      flex: 1;
      min-width: 0;
      text-align: left;

      h3 {
        font-size: 14px;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        margin: 0 0 0.25rem 0;
      }

      p {
        font-size: 13px;
        font-weight: 400;
        color: ${({ theme }) => theme.text};
        opacity: 0.7;
        margin: 0;
        line-height: 1.4;
      }
    }

    .header-action {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      flex-shrink: 0;
      margin-left: auto;

      .export-button {
        background: ${({ theme }) => theme.dropdown.background_color};
        color: ${({ theme }) => theme.text};
        border-radius: 8px;
        border: 1px solid ${({ theme }) => theme.border_color};
        cursor: pointer;
        padding: 6px 8px;
        margin: 0;
        width: auto;
        min-width: fit-content;
        height: 34px;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 6px;
        font-size: 14px;
        font-weight: 500;
        transition: all 0.2s ease-in-out;

        &:hover {
          background: ${({ theme }) => theme.button_plain.hover};
          color: ${({ theme }) => theme.text};
        }

        span {
          margin: 0;
        }

        svg:last-child {
          display: none;
        }
      }
    }
  }

  .loader-container {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .table-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    border: 1px solid #e5e5e5;
    border-radius: 0.5rem;
    overflow: hidden;
    background: ${({ theme }) => theme.widget.background};
    min-height: 0;
  }

  .table-header {
    display: flex;
    background: ${({ theme }) => theme.body};
    border-bottom: 1px solid #e5e5e5;
    font-weight: 500;
    font-size: 11px;
    color: ${({ theme }) => theme.text};
    opacity: 0.7;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    flex-shrink: 0;

    span {
      padding: 0.75rem 0.5rem;
      flex: 1;
      text-align: left;

      &:first-child {
        border-right: 1px solid #e5e5e5;
        flex: 1.2;
      }
      &:nth-child(2) {
        border-right: 1px solid #e5e5e5;
        flex: 0.8;
      }
      &:nth-child(3) {
        border-right: 1px solid #e5e5e5;
        flex: 1;
      }
      &:last-child {
        flex: 2;
      }
    }
  }

  .table-body {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
  }

  .table-row {
    display: flex;
    border-bottom: 1px solid #e5e5e5;
    font-size: 13px;
    background: ${({ theme }) => theme.widget.background};

    &:hover {
      background: ${({ theme }) => theme.body};
    }

    &:last-child {
      border-bottom: 1px solid #e5e5e5;
    }

    span {
      padding: 0.75rem 0.5rem;
      flex: 1;
      text-align: left;
      color: ${({ theme }) => theme.text};

      &:first-child {
        border-right: 1px solid #e5e5e5;
        font-weight: 500;
        flex: 1.2;
        text-transform: capitalize;
      }
      &:nth-child(2) {
        border-right: 1px solid #e5e5e5;
        flex: 0.8;
      }
      &:nth-child(3) {
        border-right: 1px solid #e5e5e5;
        flex: 1;
        font-size: 12px;
      }
      &:last-child {
        flex: 2;
        line-height: 1.3;
      }
    }
  }

  .no-data {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: ${({ theme }) => theme.text};
    opacity: 0.5;
    border: 1px solid #e5e5e5;
    border-radius: 0.5rem;
  }

  .error-screen {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;
